import { motion } from 'framer-motion';
import { useCallback, useRef } from 'react';
import {
  Check,
  Loader2,
  Clock,
  XCircle,
  Ticket,
  Mail,
  Route,
  FileWarning,
  HelpCircle,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { DocumentRequirement } from '../constants';
import type { UploadedDocument } from '../types';
import { getRequirementStatus, getDocumentsForRequirement } from '../useClaimState';

// Icon mapping for requirement icons
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Clock,
  XCircle,
  Ticket,
  Mail,
  Route,
  FileWarning,
  HelpCircle,
};

interface RequirementChecklistProps {
  requirements: DocumentRequirement[];
  documents: UploadedDocument[];
  level: 'hard' | 'soft';
  onUploadForRequirement?: (file: File, requirementId: string) => void;
}

export function RequirementChecklist({
  requirements,
  documents,
  level,
  onUploadForRequirement,
}: RequirementChecklistProps) {
  if (requirements.length === 0) return null;

  return (
    <div className="space-y-2">
      {requirements.map((req, index) => (
        <RequirementItem
          key={req.id}
          requirement={req}
          documents={documents}
          level={level}
          index={index}
          onUpload={onUploadForRequirement}
        />
      ))}
    </div>
  );
}

interface RequirementItemProps {
  requirement: DocumentRequirement;
  documents: UploadedDocument[];
  level: 'hard' | 'soft';
  index: number;
  onUpload?: (file: File, requirementId: string) => void;
}

function RequirementItem({
  requirement: req,
  documents,
  level,
  index,
  onUpload,
}: RequirementItemProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const status = getRequirementStatus(req, documents);
  const satisfyingDocs = getDocumentsForRequirement(req, documents);
  const Icon = iconMap[req.icon] || HelpCircle;
  const isClickable = status !== 'satisfied' && onUpload;

  const handleClick = useCallback(() => {
    if (isClickable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isClickable]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onUpload) {
        Array.from(files).forEach((file) => onUpload(file, req.id));
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onUpload, req.id]
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={handleClick}
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border transition-all duration-200',
        // Satisfied state
        status === 'satisfied' && 'border-green-200 bg-green-50/50',
        // Pending state (uploading)
        status === 'pending' && 'border-amber-200 bg-amber-50/50',
        // Missing state - different for hard vs soft
        status === 'missing' &&
          level === 'hard' &&
          'border-red-200 bg-red-50/30',
        status === 'missing' &&
          level === 'soft' &&
          'border-slate-200 bg-slate-50/50',
        // Clickable styling
        isClickable && 'cursor-pointer hover:shadow-md hover:scale-[1.01]'
      )}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
            {/* Status Icon */}
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                status === 'satisfied' && 'bg-green-100',
                status === 'pending' && 'bg-amber-100',
                status === 'missing' && level === 'hard' && 'bg-red-100',
                status === 'missing' && level === 'soft' && 'bg-slate-100'
              )}
            >
              {status === 'satisfied' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                  <Check className="w-4 h-4 text-green-600" />
                </motion.div>
              )}
              {status === 'pending' && (
                <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
              )}
              {status === 'missing' && (
                <Icon
                  className={cn(
                    'w-4 h-4',
                    level === 'hard' ? 'text-red-400' : 'text-slate-400'
                  )}
                />
              )}
            </div>

            {/* Requirement Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p
                  className={cn(
                    'text-sm font-medium',
                    status === 'satisfied' && 'text-green-700',
                    status === 'pending' && 'text-amber-700',
                    status === 'missing' &&
                      level === 'hard' &&
                      'text-red-700',
                    status === 'missing' &&
                      level === 'soft' &&
                      'text-slate-600'
                  )}
                >
                  {req.label}
                </p>
                {level === 'hard' && status !== 'satisfied' && (
                  <Badge
                    variant="destructive"
                    className="text-[10px] px-1.5 py-0 h-4"
                  >
                    Erforderlich
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                {req.description}
              </p>

              {/* Show which documents satisfy this requirement */}
              {status === 'satisfied' && satisfyingDocs.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {satisfyingDocs.map((doc) => (
                    <span
                      key={doc.id}
                      className="inline-flex items-center gap-1 text-[10px] text-green-600 bg-green-100 px-1.5 py-0.5 rounded"
                    >
                      <Check className="w-2.5 h-2.5" />
                      {doc.name.length > 20
                        ? doc.name.substring(0, 17) + '...'
                        : doc.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Show help hint for missing hard requirements */}
              {status === 'missing' && level === 'hard' && req.helpHint && (
                <p className="text-xs text-red-500 mt-1 italic">
                  {req.helpHint}
                </p>
              )}

              {/* Click to upload hint */}
              {isClickable && (
                <p className="text-xs text-blue-500 mt-1.5 flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  Klicken zum Hochladen
                </p>
              )}
            </div>
    </motion.div>
  );
}
